import { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Tooltip,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { usePartyManager } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  EDIT_SOURCE,
  CODEMETA_SOURCE,
} from './partyManagerUtils';

const PartyRoleField = ({
  fieldKey,
  label,
  roleKey,
  roleLabel,
  isLeadControl = false,
  leadStatusOptions = [],
  disabled = false,
  roleCheckboxDisabled = false,
  editable,
}) => {
  const {
    partyData,
    setPartyData,
    getDisplayValue,
    handleFieldChange,
    handleCycleSource,
    getAvailableSources,
    handleRoleChange,
    isPartyManagerLoading,
  } = usePartyManager();

  const displayValue = getDisplayValue(fieldKey);
  const [localValue, setLocalValue] = useState(displayValue || '');

  useEffect(() => {
    setLocalValue(displayValue || '');
  }, [displayValue]);

  const handleBlur = () => {
    if (localValue !== displayValue) {
      handleFieldChange(fieldKey, localValue, true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleFieldChange(fieldKey, localValue, true);
    }
  };

  const handleLeadStatusChange = (newStatus) => {
    setPartyData((prev) => {
      const isNowLead = ['NEW', 'PREQUALIFIED', 'QUALIFIED'].includes(
        newStatus,
      );
      const newIsCustomerState = {
        ...prev.isCustomer,
        edit: isNowLead || prev.isCustomer.edit,
        selected: 'edit',
      };
      return {
        ...prev,
        leadStatus: { ...prev.leadStatus, edit: newStatus, selected: 'edit' },
        isCustomer: newIsCustomerState,
      };
    });
  };

  if (isLeadControl) {
    const isLead = ['NEW', 'PREQUALIFIED', 'QUALIFIED'].includes(displayValue);
    const handleCheckboxChange = (event) =>
      handleLeadStatusChange(event.target.checked ? 'NEW' : 'NONE');
    const handleSelectChange = (event) =>
      handleLeadStatusChange(event.target.value);

    const converted = partyData.leadStatus?.weclapp === 'CONVERTED';

    return (
      <>
        {isPartyManagerLoading ? (
          <Skeleton variant="rounded" width="100%" height={40} />
        ) : (
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel id="lead-status-label">{label}</InputLabel>
            <Tooltip
              title={
                disabled
                  ? 'Ein bestehender Kunde kann nicht mehr als Interessent bearbeitet werden'
                  : ''
              }
            >
              <Select
                labelId="lead-status-label"
                label={label}
                value={displayValue || 'NONE'}
                onChange={handleSelectChange}
                disabled={converted}
                endAdornment={
                  <InputAdornment position="end" sx={{ mr: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isLead}
                          onChange={handleCheckboxChange}
                          disabled={disabled || converted}
                          sx={{ p: 0.5 }}
                        />
                      }
                      label="Interessent"
                      sx={{ ml: 1 }}
                    />
                  </InputAdornment>
                }
              >
                {leadStatusOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Tooltip>
          </FormControl>
        )}
      </>
    );
  }

  const roleValue = getDisplayValue(roleKey);
  const isEditable = editable ?? !disabled;
  const availableSources = getAvailableSources(fieldKey);
  const selectedSource = partyData[fieldKey]?.selected;

  const getButtonIcon = () => {
    if (selectedSource === 'edit') return EDIT_SOURCE.icon;
    if (selectedSource === 'codemeta') {
      return (
        <img
          src={CODEMETA_SOURCE.icon}
          alt={CODEMETA_SOURCE.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    const sourceInfo = POSSIBLE_SOURCES[selectedSource];
    return sourceInfo?.icon ? (
      <img
        src={sourceInfo.icon}
        alt={sourceInfo.name}
        style={{ width: '24px', height: '24px' }}
      />
    ) : (
      '?'
    );
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isPartyManagerLoading && partyData.partyType === 'PERSON' ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <>
          <TextField
            label={label}
            size="small"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={!isEditable || isPartyManagerLoading}
            sx={{ flex: 1 }}
            autoComplete="nope"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={
                          disabled ||
                          roleCheckboxDisabled ||
                          isPartyManagerLoading
                        }
                        checked={!!roleValue}
                        onChange={(e) =>
                          handleRoleChange(roleKey, e.target.checked)
                        }
                        sx={{ p: 0.5 }}
                      />
                    }
                    label={roleLabel}
                    sx={{ ml: 1, mr: 0.5 }}
                  />
                </InputAdornment>
              ),
            }}
          />
          {availableSources.length > 1 && (
            <Button
              title={`Quelle wechseln. Aktuell: ${selectedSource}. Verfügbar: ${availableSources.join(', ')}`}
              sx={{
                width: '40px',
                height: '40px',
                minWidth: '40px',
                maxWidth: '40px',
              }}
              onClick={() => handleCycleSource(fieldKey)}
              variant="outlined"
            >
              {getButtonIcon()}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default PartyRoleField;
